/**
 * Walk-in poll zone anchors, per level. Central config (not a level-instance
 * descriptor) because both the render manager and the open-gate in
 * multiplayer.js need it, and multiplayer.js has no access to the level
 * instance. Anchors are deliberately NOT part of the synced pollState - the
 * wire stays small, and a level without anchors degrades to banner-only.
 *
 * Exhibition placement: the open floor band in front of the theater stage
 * (deck center {1.5, 0, -44.5}, 28x7 - see THEATER_STAGE_CONFIG), between the
 * deck edge at z ~ -41 and the tour's viewing point at z -28.5. The prize
 * wheel sits at x 21, audience-left stays clear.
 */

/** All poll discs share one size/height; per-level maps list only centers. */
const ZONE_RADIUS = 2.0;
const ZONE_Y = 0.08;
/** Center-to-center spacing between adjacent discs on the row. */
const ZONE_SPACING = 8;

/** Row center per level id; levels absent here cannot host poll zones. */
const ZONE_ROWS = {
  'futuristic-exhibition': { x: 0, z: -33 },
};

/**
 * Zone anchors for an N-option poll in a level, or null when the level has
 * none. The N discs sit on one row, re-centered so 2 options -> x +-4,
 * 3 -> -8/0/8, 4 -> -12/-4/4/12 (with the exhibition row at x 0).
 *
 * @param {string|null|undefined} levelId
 * @param {number} optionCount
 * @returns {Array<{x: number, z: number, y: number, radius: number}>|null}
 */
export function getPollZoneAnchors(levelId, optionCount) {
  const row = typeof levelId === 'string' ? ZONE_ROWS[levelId] : undefined;
  if (!row) return null;
  if (!Number.isInteger(optionCount) || optionCount < 1 || optionCount > 4) return null;
  const anchors = [];
  const startX = row.x - ((optionCount - 1) * ZONE_SPACING) / 2;
  for (let i = 0; i < optionCount; i++) {
    anchors.push({ x: startX + i * ZONE_SPACING, z: row.z, y: ZONE_Y, radius: ZONE_RADIUS });
  }
  return anchors;
}

/** Does this level support hosting poll zones at all? */
export function levelSupportsPolls(levelId) {
  return typeof levelId === 'string' && !!ZONE_ROWS[levelId];
}
